import { 
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger 
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { HelpCircle, Award } from "lucide-react";
import { 
  BADGE_CATEGORY_METADATA, 
  getAllBadgeCategories, 
  getBadgeCategoryIcon,
  getCategoryAccentColor,
  getCategoryName,
  getCategoryDescription,
  getCategoryTiers,
  TIER_COLORS
} from "@/lib/badgeUtils";

interface BadgeExplainerModalProps {
  trigger?: React.ReactNode;
}

export default function BadgeExplainerModal({ trigger }: BadgeExplainerModalProps) {
  return (
    <Dialog>
      <DialogTrigger asChild>
        {trigger || (
          <Button variant="outline" size="sm" data-testid="button-badge-explainer">
            <HelpCircle className="w-4 h-4 mr-2" />
            How Badges Work
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[90svh]" data-testid="dialog-badge-explainer">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Award className="w-5 h-5 text-kudoz" />
            Badge Categories & Tiers
          </DialogTitle>
        </DialogHeader>

        <ScrollArea className="h-[70svh] pr-4">
          <div className="space-y-6">
            {/* Introduction */}
            <div className="text-sm text-muted-foreground">
              <p>
                Earn badges by completing various activities on Eatzy Kudoz! Each badge category has 4 tiers,
                and you'll progress automatically as you use the app.
              </p>
            </div>

            <Separator />

            {/* Badge Categories */}
            <div className="space-y-4">
              {getAllBadgeCategories().filter(categoryId => categoryId !== 'tip' && categoryId !== 'profile').map((categoryId) => {
                const IconComponent = getBadgeCategoryIcon(categoryId);
                const accentColor = getCategoryAccentColor(categoryId);
                const name = getCategoryName(categoryId);
                const description = getCategoryDescription(categoryId);
                const tiers = getCategoryTiers(categoryId);

                return (
                  <Card key={categoryId} data-testid={`badge-category-${categoryId}`}>
                    <CardContent className="p-4 space-y-3">
                      {/* Category Header */}
                      <div className="flex items-start gap-3">
                        <div
                          className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0"
                          style={{ backgroundColor: `${accentColor}15` }}
                        >
                          <IconComponent className="w-6 h-6" style={{ color: accentColor }} />
                        </div>
                        <div className="flex-1">
                          <h3 className="font-semibold text-base">{name}</h3>
                          <p className="text-sm text-muted-foreground">{description}</p>
                        </div>
                      </div>

                      {/* Tier Progression */}
                      <div className="grid grid-cols-2 gap-2">
                        {tiers.map((tier, index) => {
                          const tierNames = ['bronze', 'silver', 'gold', 'platinum'];
                          const tierColor = TIER_COLORS[tierNames[index] as keyof typeof TIER_COLORS];
                          
                          return (
                            <div
                              key={tier.name}
                              className="flex items-start gap-2 p-2 rounded bg-muted/30"
                              data-testid={`tier-${categoryId}-${index}`}
                            >
                              <Badge variant="outline" className={`${tierColor} flex-shrink-0 capitalize`}>
                                {tierNames[index]}
                              </Badge>
                              <div className="text-xs space-y-0.5 min-w-0">
                                <div className="font-medium">{tier.name}</div>
                                <div className="text-muted-foreground">{tier.requirement}</div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>

            {/* Footer Note */}
            <div className="text-xs text-muted-foreground text-center pt-4 border-t">
              <p>Badges are earned automatically as you use Eatzy Kudoz. Keep exploring, reviewing, and ordering!</p>
            </div>
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}
